import { StepHeader, WizardLayout } from "@/components/agents/setup/shell"
import { FormField } from "@/components/form-field"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"

// IMP-373: a single optional free-text field where the business tailors an
// agent's behavior. Shared across all four agents' setup wizards (a
// second-to-last step) and their edit drawers (a Customization tab); each agent
// supplies its own role-specific placeholder. The value populates a dedicated
// custom-instructions section of that agent's prompt; clearing it drops the
// section.

const DESCRIPTION =
  "Your agent follows these in addition to its standard behavior. Optional — you can skip this and add it later."

export function CustomInstructionsField({
  value,
  placeholder,
  onChange,
}: {
  value: string
  placeholder: string
  onChange: (value: string) => void
}) {
  return (
    <FormField label="Custom instructions" htmlFor="custom-instructions">
      <Textarea
        id="custom-instructions"
        rows={7}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </FormField>
  )
}

export function CustomInstructionsStep({
  stepNumber,
  totalSteps,
  value,
  placeholder,
  onChange,
  onNext,
  onBack,
  nextLabel = "Continue",
  processing = false,
}: {
  stepNumber: number
  totalSteps: number
  value: string
  placeholder: string
  onChange: (value: string) => void
  onNext: () => void
  onBack: () => void
  nextLabel?: string
  processing?: boolean
}) {
  return (
    <WizardLayout
      footer={
        <>
          <Button variant="outline" onClick={onBack} disabled={processing}>
            Back
          </Button>
          <Button onClick={onNext} disabled={processing}>
            {nextLabel}
          </Button>
        </>
      }
    >
      <StepHeader
        eyebrow={`Step ${stepNumber} of ${totalSteps}`}
        title="Custom instructions"
        description={DESCRIPTION}
      />
      <CustomInstructionsField
        value={value}
        placeholder={placeholder}
        onChange={onChange}
      />
    </WizardLayout>
  )
}

// Role-specific placeholder examples (verbatim from IMP-373).
export const CUSTOM_INSTRUCTIONS_PLACEHOLDERS = {
  receptionist:
    "e.g. We're open during major holidays. We don't book appointments before 9am. If someone asks about parking, there's a free lot behind the building.",
  "website-chat":
    "e.g. Keep replies to two or three sentences. If someone asks for a quote, collect their email before answering. Turn around times are typically two weeks.",
  "lead-responder":
    "e.g. Treat anyone mentioning a budget over $5k as high priority. Ask for the project timeline and address before marking a lead qualified. Don't follow up more than twice.",
  "review-responder":
    "e.g. Thank every reviewer by name. For anything under three stars, apologize and offer to make it right offline. Keep responses warm but brief.",
} as const
